import { v4 as uuidv4 } from 'uuid'
import { Segment } from '@meowwolf/cookie-consent-utils'
import { MW_SID, SEGMENT_USER_TIMEOUT_MS } from '@/config/tracking.config'
import { withTimeout } from './promise.utils'
import { safeLocalStorage } from './storage.utils'

/**
 * Retrieves the anonymous segment ID associated with the current user.
 * This function checks if both `window` and `analytics` objects are defined in the global scope.
 * If so, it attempts to access the anonymous ID from the analytics user object.
 *
 * `AnalyticsBrowser.user()` only settles once Segment has loaded, and the consent wrapper can
 * hold that off indefinitely, so the wait is capped at `SEGMENT_USER_TIMEOUT_MS`. Callers sit
 * on user actions like add-to-cart and must never hang on Segment.
 *
 * @param {number} timeoutMs - How long to wait for Segment; callers off the user's path can afford longer.
 * @returns {Promise<Segment.ID | undefined>} The anonymous ID if available; otherwise, undefined.
 */
export const getAnonymousSegmentId = async (timeoutMs = SEGMENT_USER_TIMEOUT_MS): Promise<Segment.ID | undefined> => {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return undefined

  if (typeof window.analytics !== 'undefined') {
    const user = await withTimeout(window.analytics.user?.(), timeoutMs, undefined)
    return user?.anonymousId?.()
  }
  return undefined
}

/** Session id for this page view, used when localStorage is unavailable */
let inMemorySessionId: string | undefined

/**
 * Create "session" id and store it in local storage
 * Fetch from local storage or generate with uuidv4.
 * When local storage is unavailable, the id lasts for this page view.
 * @returns {string} - "session" id
 */
export const getSessionId = (): string => {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return ''

  const sid = safeLocalStorage.getItem(MW_SID)
  if (sid) {
    return sid
  }
  inMemorySessionId ??= uuidv4()
  safeLocalStorage.setItem(MW_SID, inMemorySessionId)
  return inMemorySessionId
}

/**
 * Retrieves the anonymous ID for the current session.
 * This function calls `getSessionId()` to obtain the ID.
 *
 * @returns {string} The anonymous ID for the current session.
 */
export const getAnonymousId = (): string => getSessionId()
